export type SupportSettingToggle = {
  enabled: boolean;
};

/**
 * Runtime feature kill switch in svc-support settings.
 * enabled: true → on for everyone. enabled: false → only bypassRequestors; empty list → off for everyone.
 */
export type SupportFeatureSetting = SupportSettingToggle & {
  bypassRequestors: string[];
};

/** svc-zendesk SIERRA_BOOKING_ENQUIRY: bypassRequestors get every reason, enabled only the ticked ones. */
export type BookingEnquirySetting = SupportFeatureSetting & {
  reasons: string[];
  /** Send the other enquiries to Sierra too and post its reply to Slack only. */
  shadow?: boolean;
};

export const BOOKING_ENQUIRY_REASONS = [
  { value: 'inclusions', label: 'Inclusions Information' },
  { value: 'booking_details', label: 'Booking Details Request' },
  { value: 'fine_print', label: 'Fine Print Clarification' },
  { value: 'reassurance', label: 'Reassurance of Confirmed Booking' },
  { value: 'transfers', label: 'Transfer Information' },
] as const;

export type LexCategoryMode = 'off' | 'shadow' | 'live';

/**
 * svc-zendesk SIERRA_LEX_CATEGORIES: what LEX does with each category (see LEX_CATEGORY_GROUPS).
 * off: agents handle it. shadow: agents handle it and LEX's unsent reply goes to Slack. live: LEX replies.
 * testEmails always get live. Categories not listed keep their existing routing.
 */
export type LexCategoriesSetting = {
  modes: Record<string, LexCategoryMode>;
  testEmails: Array<string>;
};

export const LEX_CATEGORY_MODES: Array<{ value: LexCategoryMode; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'live', label: 'Live' },
];

/**
 * Each group is one LEX flow; each category is a key svc-zendesk matches on: a Zendesk ticket reason slug, or
 * `category:<AI category>` for a whole AI category. Groups with inUse: false are shown but svc-zendesk doesn't
 * read them yet. modes lists what the flow supports (no Shadow where svc-zendesk has no shadow test).
 * Keep in sync with svc-zendesk.
 */
export const LEX_CATEGORY_GROUPS = [
  {
    label: 'Special requests',
    description: 'LEX sends special requests to the hotel on Flash bookings and passes everything else to an agent.',
    inUse: true,
    modes: ['off', 'shadow', 'live'],
    categories: [{ key: 'ticket_reason_change_booking__order_details_update', label: 'Order Details Update' }],
  },
  {
    label: 'Cancellations',
    description:
      'LEX cancels within-policy bookings, offers ROOP or an insurance letter outside policy, and escalates the rest. Covers every Cancel Booking email, including ones whose policy is not known yet.',
    inUse: false,
    modes: ['off', 'live'],
    categories: [{ key: 'category:Cancel Booking', label: 'Cancel Booking' }],
  },
  {
    label: 'Booking enquiries',
    description: 'LEX answers questions about a booking the customer holds from its order details.',
    inUse: false,
    modes: ['off', 'shadow', 'live'],
    categories: [
      { key: 'ticket_reason_booking_support__inclusions_information', label: 'Inclusions Information' },
      { key: 'ticket_reason_booking_support__booking_details_request', label: 'Booking Details Request' },
      { key: 'ticket_reason_booking_support__fine_print_clarification', label: 'Fine Print Clarification' },
      {
        key: 'ticket_reason_booking_support__reassurance_of_confirmed_booking',
        label: 'Reassurance of Confirmed Booking',
      },
      { key: 'ticket_reason_booking_support__transfer_information', label: 'Transfer Information' },
    ],
  },
] as const;

export type BypassableFeatureKey =
  | 'SIERRA_EMAIL_AI'
  | 'SALES_INTENT_ROUTING'
  | 'FLIGHT_INTENT_ROUTING'
  | 'SUPPORT_INTENT_CLARIFICATION'
  | 'CUSTOMER_AUTO_SOLVE'
  | 'CUSTOMER_PARTNERSHIP_DEFLECT'
  | 'CUSTOMER_REPLY_MERGE'
  | 'SIERRA_BOOKING_ENQUIRY';

export const BYPASSABLE_FEATURE_KEYS = [
  'SIERRA_EMAIL_AI',
  'SALES_INTENT_ROUTING',
  'FLIGHT_INTENT_ROUTING',
  'SUPPORT_INTENT_CLARIFICATION',
  'CUSTOMER_AUTO_SOLVE',
  'CUSTOMER_PARTNERSHIP_DEFLECT',
  'CUSTOMER_REPLY_MERGE',
  'SIERRA_BOOKING_ENQUIRY',
] as const satisfies readonly BypassableFeatureKey[];

export const DEFAULT_BYPASSABLE_FEATURE_SETTING: SupportFeatureSetting = {
  enabled: false,
  bypassRequestors: [],
};

export interface SettingsFormData {
  GENESYS: SupportSettingToggle;
  CHRISTMAS: SupportSettingToggle;
  SIERRA_EMAIL_AI: SupportFeatureSetting;
  SALES_INTENT_ROUTING: SupportFeatureSetting;
  FLIGHT_INTENT_ROUTING: SupportFeatureSetting;
  SUPPORT_INTENT_CLARIFICATION: SupportFeatureSetting;
  CUSTOMER_AUTO_SOLVE: SupportFeatureSetting;
  CUSTOMER_PARTNERSHIP_DEFLECT: SupportFeatureSetting;
  CUSTOMER_REPLY_MERGE: SupportFeatureSetting;
  SIERRA_BOOKING_ENQUIRY: BookingEnquirySetting;
  SIERRA_LEX_CATEGORIES: LexCategoriesSetting;
}

export const DEFAULT_SETTINGS: SettingsFormData = {
  GENESYS: {
    enabled: false,
  },
  CHRISTMAS: {
    enabled: false,
  },
  SIERRA_EMAIL_AI: { ...DEFAULT_BYPASSABLE_FEATURE_SETTING, enabled: true },
  // Keep in sync with svc-zendesk supportSettings fail-closed defaults.
  SALES_INTENT_ROUTING: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  FLIGHT_INTENT_ROUTING: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  SUPPORT_INTENT_CLARIFICATION: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_AUTO_SOLVE: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_PARTNERSHIP_DEFLECT: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_REPLY_MERGE: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  SIERRA_BOOKING_ENQUIRY: { ...DEFAULT_BYPASSABLE_FEATURE_SETTING, reasons: [], shadow: false },
  SIERRA_LEX_CATEGORIES: { modes: {}, testEmails: [] },
};
